import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    useTheme,
    Skeleton,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { getClientsDashboard, ClientsDashboardSummary } from '../../services/clients-service';

const AdminDashboardPage: React.FC = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState<ClientsDashboardSummary | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        getClientsDashboard()
            .then((data) => {
                if (!cancelled) {
                    setSummary(data);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Failed to load dashboard');
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary }}>
                    Admin Dashboard
                </Typography>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const cards = [
        {
            title: 'Total Clients',
            value: summary?.totalClients ?? 0,
            icon: <BusinessIcon />,
            color: theme.palette.primary.main,
        },
        {
            title: 'Active',
            value: summary?.activeClients ?? 0,
            icon: <CheckCircleIcon />,
            color: theme.palette.success.main,
        },
        {
            title: 'Inactive',
            value: summary?.inactiveClients ?? 0,
            icon: <CancelIcon />,
            color: theme.palette.text.secondary,
        },
        {
            title: 'Signed up (last 30 days)',
            value: summary?.recentSignUpsCount ?? 0,
            icon: <TrendingUpIcon />,
            color: theme.palette.info.main,
        },
    ];

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                Admin Dashboard
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
                {cards.map((card) => (
                    <Grid item xs={12} sm={6} md={3} key={card.title}>
                        <Card
                            sx={{
                                height: '100%',
                                borderLeft: `4px solid ${card.color}`,
                                bgcolor: theme.palette.background.paper,
                            }}
                        >
                            <CardContent>
                                {loading ? (
                                    <>
                                        <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
                                        <Skeleton variant="text" width="40%" height={36} />
                                    </>
                                ) : (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                                                {card.title}
                                            </Typography>
                                            <Box sx={{ color: card.color, display: 'flex', alignItems: 'center' }}>
                                                {card.icon}
                                            </Box>
                                        </Box>
                                        <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                                            {card.value.toLocaleString()}
                                        </Typography>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default AdminDashboardPage;
